
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Clock, BookOpen } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { contactFormSchema, ContactFormValues } from "@/utils/forms/contactFormSchema";

interface GammaEmbedProps {
  className?: string;
}

const GammaEmbed: React.FC<GammaEmbedProps> = ({ className = "" }) => {
  const [showForm, setShowForm] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pdfUrl = "https://qcgnklqgyzwvjrmhzwfj.supabase.co/storage/v1/object/sign/insights/AI-Oversight-at-Board-Level-The-Forward-Looking-Duty-You-Cant-Delegate%20(2).pdf?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6InN0b3JhZ2UtdXJsLXNpZ25pbmcta2V5XzM0NzQ3N2ExLTRmMzEtNGE0ZC1hYTdmLWJiNGVhYzFhNzMwYiJ9.eyJ1cmwiOiJpbnNpZ2h0cy9BSS1PdmVyc2lnaHQtYXQtQm9hcmQtTGV2ZWwtVGhlLUZvcndhcmQtTG9va2luZy1EdXR5LVlvdS1DYW50LURlbGVnYXRlICgyKS5wZGYiLCJpYXQiOjE3NDcxNjU5ODgsImV4cCI6MTc3ODcwMTk4OH0.Gm3l8fF68RjQKyFjx2cZIgo9sANLT7mNGPfvLTEWubk";

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      title: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    
    try {
      // Store the user information in Supabase
      const { error } = await supabase.from("contacts").insert({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        company: data.company,
        title: data.title,
        lead_list_name: "pdf_download",
        submitted_at: new Date().toISOString() // Add timestamp
      });

      if (error) throw error;
      
      // Show the PDF
      setShowPdf(true);
      setShowForm(false);
      
      toast({
        title: "Success!",
        description: "The document is now available for viewing.",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      
      // Always provide access to the PDF, even if there's an error
      setShowPdf(true);
      setShowForm(false);
      
      toast({
        title: "Document Available",
        description: "Your requested document is now ready to view.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`w-full flex flex-col items-center h-full ${className}`}>
      {!showPdf ? (
        <div className="bg-gradient-to-br from-white to-techne-lightestGray rounded-xl shadow-xl border border-gray-100 p-0 w-full overflow-hidden animate-fade-in h-full">
          {/* Top Banner */}
          <div className="bg-gradient-to-r from-techne-primaryPurple to-techne-deepPurple text-white py-2 px-4 text-sm font-medium flex justify-between items-center">
            <span>EXCLUSIVE CONTENT</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> 15 min read
            </span>
          </div>
          
          <div className="p-8 flex flex-col h-full">
            {/* Image */}
            <div className="mb-4">
              <div className="rounded-lg overflow-hidden shadow-lg border border-gray-200 mb-4 transform transition-transform hover:scale-105">
                <img 
                  src="/lovable-uploads/08ee0fc7-0ab6-4898-8a45-f995143276e7.png" 
                  alt="AI Oversight at Board Level" 
                  className="w-full h-auto"
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex flex-col flex-grow">
              <h2 className="text-2xl font-bold leading-tight">
                <span className="text-techne-primaryPurple block mb-1">White Paper:</span>
                AI Oversight at Board Level
              </h2>
              
              <h3 className="text-lg text-gray-700 font-medium mb-4">The Forward-Looking Duty You Can't Delegate</h3>
              
              <div className="bg-white/80 rounded-lg p-4 mb-6 border-l-4 border-techne-primaryPurple shadow-sm flex-grow">
                <p className="text-gray-700 font-medium mb-3">Key insights from this white paper:</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex gap-2">
                    <BookOpen className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />
                    <span>Legal and fiduciary responsibilities for board AI governance</span>
                  </li>
                  <li className="flex gap-2">
                    <BookOpen className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />
                    <span>Practical frameworks for effective AI oversight</span>
                  </li>
                  <li className="flex gap-2">
                    <BookOpen className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />
                    <span>Risk mitigation strategies for AI implementation</span>
                  </li>
                </ul>
              </div>
              
              <Button 
                onClick={() => setShowForm(true)}
                size="lg"
                className="w-full md:w-auto bg-techne-primaryPurple hover:bg-techne-deepPurple text-white transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 mt-auto"
              >
                <Download className="h-4 w-4" />
                Get Exclusive Access
              </Button>
              
              <p className="text-xs text-gray-500 mt-3 text-center md:text-left">
                Enter your business email to receive this white paper instantly.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="animate-fade-in w-full h-full">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full">
            <div className="bg-gradient-to-r from-techne-primaryPurple to-techne-deepPurple py-4 px-6">
              <h2 className="text-white text-xl font-semibold">AI Oversight at Board Level</h2>
              <p className="text-white/90 text-sm">The Forward-Looking Duty You Can't Delegate</p>
            </div>
            <div className="p-4 h-[600px]">
              <iframe 
                src={pdfUrl}
                style={{ width: "100%", height: "100%" }}
                allow="fullscreen" 
                title="AI Oversight at Board Level: The Forward-Looking Duty You Can't Delegate"
                className="rounded-lg border border-gray-200"
              ></iframe>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Access White Paper</DialogTitle>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Smith" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@company.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Corporation" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Role</FormLabel>
                    <FormControl>
                      <Input placeholder="Board Member, CEO, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Processing..." : "Access White Paper"}
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                By submitting, you agree to our privacy policy and consent to receive communications from us.
              </p>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GammaEmbed;
